import * as signalR from '@microsoft/signalr';
import { resolveApiBaseUrl } from '@/lib/config/apiBaseUrl';
import logger from '@/lib/logger';

export interface ChannelClassificationUpdate {
  testId: string;
  channelId: number;
  status: 'positive' | 'negative' | 'invalid';
  confidence: number;
  concentration?: number;
  markerIds: number[];
}

export interface TestCompleteUpdate {
  testId: string;
  completedAt: string;
}

export interface ClassificationRetryingUpdate {
  testId: string;
  attemptNumber: number;
  maxAttempts: number;
  error: string;
}

export interface ClassificationFailedUpdate {
  testId: string;
  error: string;
  failedAt: string;
}

export interface TestStatusChangedUpdate {
  testId: string;
  status: string;
  timestamp: string;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private reconnectAttempts = 0;
  private reconnectedCallbacks = new Set<() => void>();

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      logger.trace('[SignalR] Already connected');
      return;
    }

    const baseUrl = resolveApiBaseUrl();

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/classification`, {
        withCredentials: true, // For CORS with credentials
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => {
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts++), 30000);
          logger.trace(`[SignalR] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
          return delay;
        }
      })
      .configureLogging(signalR.LogLevel.Error)
      .build();

    this.connection.onreconnecting((error) => {
      logger.warn('[SignalR] Reconnecting...', error);
    });

    this.connection.onreconnected((connectionId) => {
      logger.trace('[SignalR] Reconnected:', connectionId);
      this.reconnectAttempts = 0;
      this.reconnectedCallbacks.forEach((callback) => callback());
    });

    this.connection.onclose((error) => {
      logger.trace('[SignalR] Connection closed', error);
    });

    try {
      await this.connection.start();
      logger.debug('[SignalR] Connected successfully');
    } catch (error) {
      logger.error('[SignalR] Connection failed:', error);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      logger.trace('[SignalR] Disconnected');
    }
  }

  subscribeToChannelClassification(
    testId: string,
    callback: (update: ChannelClassificationUpdate) => void
  ): () => void {
    if (!this.connection) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    // Subscribe to all channels for this test
    const handlers: Array<() => void> = [];
    
    // Dynamically subscribe to channels 1-4
    for (let channelId = 1; channelId <= 4; channelId++) {
      const eventName = `classification:${testId}:channel:${channelId}`;
      const handler = (data: ChannelClassificationUpdate) => {
        logger.trace(`[SignalR] Event received: ${eventName}`, data);
        callback(data);
      };

      this.connection.on(eventName, handler);
      handlers.push(() => this.connection?.off(eventName, handler));
    }

    logger.trace(`[SignalR] Subscribed to classification updates for test ${testId}`);

    // Return unsubscribe function
    return () => {
      handlers.forEach(unsub => unsub());
      logger.trace(`[SignalR] Unsubscribed from classification updates for test ${testId}`);
    };
  }

  subscribeToTestComplete(
    testId: string,
    callback: (update: TestCompleteUpdate) => void
  ): () => void {
    if (!this.connection) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = `classification:${testId}:complete`;
    const handler = (data: TestCompleteUpdate) => {
      logger.trace(`[SignalR] Test complete: ${eventName}`, data);
      callback(data);
    };

    this.connection.on(eventName, handler);
    logger.trace(`[SignalR] Subscribed to test complete for ${testId}`);

    return () => {
      this.connection?.off(eventName, handler);
      logger.trace(`[SignalR] Unsubscribed from test complete for ${testId}`);
    };
  }

  /**
   * Subscribe to all test completion events globally.
   * This uses a wildcard pattern match on event names.
   * Note: SignalR doesn't support true wildcards, so we register a handler
   * that matches the pattern prefix.
   */
  subscribeToAllTestCompletions(
    callback: (update: TestCompleteUpdate) => void
  ): () => void {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    // Use a generic handler name that the backend broadcasts to
    // Backend should emit "test:classified" or we listen to specific pattern
    const eventName = 'test:classified';
    const handler = (data: TestCompleteUpdate) => {
      logger.trace('[SignalR] Global test completion:', data);
      callback(data);
    };

    this.connection.on(eventName, handler);
    logger.trace('[SignalR] Subscribed to all test completions');

    return () => {
      this.connection?.off(eventName, handler);
      logger.trace('[SignalR] Unsubscribed from all test completions');
    };
  }

  /**
   * Subscribe to all channel classification events globally.
   * This listens to a generic event that the backend emits for every channel classification.
   */
  subscribeToAllChannelClassifications(
    callback: (update: ChannelClassificationUpdate) => void
  ): () => void {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    // Use a generic handler name that the backend broadcasts to
    const eventName = 'channel:classified';
    const handler = (data: ChannelClassificationUpdate) => {
      logger.trace('[SignalR] Global channel classification:', data);
      callback(data);
    };

    this.connection.on(eventName, handler);
    logger.trace('[SignalR] Subscribed to all channel classifications');

    return () => {
      this.connection?.off(eventName, handler);
      logger.trace('[SignalR] Unsubscribed from all channel classifications');
    };
  }

  getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  /**
   * Subscribe to connection-recovered notifications, e.g. to re-run a reconciliation
   * fetch after events may have been missed while disconnected.
   */
  onReconnected(callback: () => void): () => void {
    this.reconnectedCallbacks.add(callback);
    return () => { this.reconnectedCallbacks.delete(callback); };
  }

  /**
   * Subscribe to classification retry events globally.
   * Emitted when a classification attempt fails but retries remain.
   */
  subscribeToClassificationRetrying(
    callback: (update: ClassificationRetryingUpdate) => void
  ): () => void {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = 'test:classification_retrying';
    const handler = (data: ClassificationRetryingUpdate) => {
      logger.debug('[SignalR] Classification retrying:', data);
      callback(data);
    };

    this.connection.on(eventName, handler);
    logger.trace('[SignalR] Subscribed to classification retrying events');

    return () => {
      this.connection?.off(eventName, handler);
      logger.trace('[SignalR] Unsubscribed from classification retrying events');
    };
  }

  /**
   * Subscribe to classification permanent failure events globally.
   * Emitted when all retry attempts are exhausted.
   */
  subscribeToClassificationFailed(
    callback: (update: ClassificationFailedUpdate) => void
  ): () => void {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = 'test:classification_failed';
    const handler = (data: ClassificationFailedUpdate) => {
      logger.debug('[SignalR] Classification failed:', data);
      callback(data);
    };

    this.connection.on(eventName, handler);
    logger.trace('[SignalR] Subscribed to classification failed events');

    return () => {
      this.connection?.off(eventName, handler);
      logger.trace('[SignalR] Unsubscribed from classification failed events');
    };
  }

  /**
   * Subscribe to classification retry events for a specific test.
   */
  subscribeToTestClassificationRetrying(
    testId: string,
    callback: (update: ClassificationRetryingUpdate) => void
  ): () => void {
    if (!this.connection) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = 'test:classification_retrying';
    const handler = (data: ClassificationRetryingUpdate) => {
      if (data.testId === testId) {
        callback(data);
      }
    };

    this.connection.on(eventName, handler);
    return () => { this.connection?.off(eventName, handler); };
  }

  /**
   * Subscribe to classification failure events for a specific test.
   */
  subscribeToTestClassificationFailed(
    testId: string,
    callback: (update: ClassificationFailedUpdate) => void
  ): () => void {
    if (!this.connection) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = 'test:classification_failed';
    const handler = (data: ClassificationFailedUpdate) => {
      if (data.testId === testId) {
        callback(data);
      }
    };

    this.connection.on(eventName, handler);
    return () => { this.connection?.off(eventName, handler); };
  }

  /**
   * Subscribe to test status change events for a specific test.
   * Emitted when a test's status changes (e.g. after classification completes).
   */
  subscribeToTestStatusChange(
    testId: string,
    callback: (update: TestStatusChangedUpdate) => void
  ): () => void {
    if (!this.connection) {
      throw new Error('SignalR not connected. Call connect() first.');
    }

    const eventName = 'test:status_changed';
    const handler = (data: TestStatusChangedUpdate) => {
      if (data.testId === testId) {
        callback(data);
      }
    };

    this.connection.on(eventName, handler);
    return () => { this.connection?.off(eventName, handler); };
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
